"use client";

import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { FacilitySwitcher } from "@/components/facility-switcher";
import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import { NavigationFavorites } from "@/components/navigation-favorites";
import { NavigationQuickCreate } from "@/components/navigation-quick-create";
import { NavigationSearch } from "@/components/navigation-search";
import {
  NavigationSettings,
  NavigationSettingsEntry,
} from "@/components/navigation-settings";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useFacility } from "@/contexts/FacilityContext";
import {
  flattenAdminNavigation,
  isAdminNavigationPathActive,
  resolveAdminNavigation,
  resolveAdminQuickActions,
  type AdminNavigationContext,
} from "@/lib/adminNavigation";
import { appendFacilityScopeToUrl } from "@/lib/facilityModeAccess";
import { isPlatformOwner } from "@/lib/platformRoles";
import { hasEffectivePermission } from "@/lib/staffPermissions";
import { directDebitRepository } from "@/repository/directDebitRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { UserInfo } from "@/types/common";
import { useStaffMode } from "@/contexts/StaffModeContext";
import { getPlatformRole } from "@/lib/staffMode";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useNavigationPreferences } from "@/hooks/use-navigation-preferences";
import {
  TICKETING_SETTING_CHANGED_EVENT,
  ticketingRepository,
  type TicketingSetting,
} from "@/repository/ticketingRepository";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpen } = useSidebar();
  const lastOperationalLocation = React.useRef("/dashboard");
  const wasSettingsMode = React.useRef(false);
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(() =>
    localAuthRepository.getUserInfo()
  );
  const [isDirectDebitEnabled, setIsDirectDebitEnabled] = React.useState(false);
  const [isTicketingEnabled, setIsTicketingEnabled] = React.useState(false);
  const {
    selectedFacilityId,
    facilities,
    isLoading: facilitiesLoading,
  } = useFacility();
  // 組織全体では担当施設の実効方式の和集合。組織の既定値だけで
  // 受講者対応の施設を隠さず、施設選択時はその施設だけで判断する。
  const supportsChildren =
    !facilitiesLoading &&
    facilities.some(
      (facility) =>
        (selectedFacilityId === "all" || facility.id === selectedFacilityId) &&
        (facility.effectiveMemberManagementMode === "CHILD_ONLY" ||
          facility.effectiveMemberManagementMode === "SELF_AND_CHILD")
    );
  const {
    mode,
    canUseTenantMode,
    canUsePlatformMode,
    isSwitching,
    switchMode,
  } = useStaffMode();
  const { toast } = useToast();

  React.useEffect(() => {
    const syncUserInfo = () => setUserInfo(localAuthRepository.getUserInfo());
    window.addEventListener("authStateChanged", syncUserInfo);
    return () => window.removeEventListener("authStateChanged", syncUserInfo);
  }, []);

  const isPlatformUser = mode === "platform" && canUsePlatformMode;
  const isPlatformOwnerUser = isPlatformOwner(getPlatformRole(userInfo));
  const isOrganizationAdmin = userInfo?.role === "organization_admin";
  const hasGlobalFacilityScope = userInfo?.facilityScope === "GLOBAL";
  const canManageStaff = hasEffectivePermission(
    userInfo?.role,
    "staff.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageRoles = hasEffectivePermission(
    userInfo?.role,
    "role.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageMembers = hasEffectivePermission(
    userInfo?.role,
    "member.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageCourses = hasEffectivePermission(
    userInfo?.role,
    "course.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageInventory = hasEffectivePermission(
    userInfo?.role,
    "inventory.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageBilling = hasEffectivePermission(
    userInfo?.role,
    "billing.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canExecuteBulkOperations = hasEffectivePermission(
    userInfo?.role,
    "bulk_operation.execute",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canPublishNotifications = hasEffectivePermission(
    userInfo?.role,
    "notification.publish",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );
  const canManageInquiries = hasEffectivePermission(
    userInfo?.role,
    "inquiry.manage",
    userInfo?.effectivePermissions,
    userInfo?.permissions
  );

  React.useEffect(() => {
    let cancelled = false;
    if (isPlatformUser || !canManageBilling || !hasGlobalFacilityScope) {
      setIsTicketingEnabled(false);
      return () => {
        cancelled = true;
      };
    }

    const syncTicketingSettings = (event: Event) => {
      const settings = (event as CustomEvent<TicketingSetting>).detail;
      if (!cancelled && settings) setIsTicketingEnabled(settings.enabled);
    };
    ticketingRepository
      .getSetting()
      .then((setting) => {
        if (!cancelled) setIsTicketingEnabled(setting.enabled);
      })
      .catch(() => {
        if (!cancelled) setIsTicketingEnabled(false);
      });
    window.addEventListener(
      TICKETING_SETTING_CHANGED_EVENT,
      syncTicketingSettings
    );
    return () => {
      cancelled = true;
      window.removeEventListener(
        TICKETING_SETTING_CHANGED_EVENT,
        syncTicketingSettings
      );
    };
  }, [canManageBilling, hasGlobalFacilityScope, isPlatformUser]);

  React.useEffect(() => {
    let cancelled = false;
    if (isPlatformUser) {
      setIsDirectDebitEnabled(false);
      return () => {
        cancelled = true;
      };
    }
    directDebitRepository
      .getConfig()
      .then((config) => {
        if (!cancelled) setIsDirectDebitEnabled(config.enabled);
      })
      .catch(() => {
        if (!cancelled) setIsDirectDebitEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatformUser]);

  const userData = React.useMemo(() => {
    if (!userInfo) {
      return { name: "ユーザー", email: "読み込み中…", avatar: "" };
    }
    const last = userInfo.last_name ?? userInfo.lastName;
    const first = userInfo.first_name ?? userInfo.firstName;
    const composed = [last, first].filter(Boolean).join(" ").trim();
    const displayName = composed || userInfo.full_name || "ユーザー";
    return {
      name: displayName,
      email: userInfo.email,
      avatar: userInfo.avatarUrl ?? "",
    };
  }, [userInfo]);

  const handleModeSwitch = async () => {
    const nextMode = isPlatformUser ? "tenant" : "platform";
    try {
      await switchMode(nextMode);
    } catch {
      toast({
        title: "モードを切り替えられませんでした",
        description: "通信状態を確認して、もう一度お試しください。",
        variant: "destructive",
      });
    }
  };

  const navigationContext = React.useMemo<AdminNavigationContext>(
    () => ({
      selectedFacilityId,
      supportsChildren,
      isPlatformUser,
      isPlatformOwner: isPlatformOwnerUser,
      isOrganizationAdmin,
      hasGlobalFacilityScope,
      canManageStaff,
      canManageRoles,
      canManageMembers,
      canManageCourses,
      canManageInventory,
      canManageBilling,
      canExecuteBulkOperations,
      canPublishNotifications,
      canManageInquiries,
      isDirectDebitEnabled,
      isTicketingEnabled,
    }),
    [
      canExecuteBulkOperations,
      canManageBilling,
      canManageCourses,
      canManageInventory,
      canManageMembers,
      canManageRoles,
      canManageStaff,
      canPublishNotifications,
      canManageInquiries,
      hasGlobalFacilityScope,
      isDirectDebitEnabled,
      isOrganizationAdmin,
      isPlatformOwnerUser,
      isPlatformUser,
      isTicketingEnabled,
      selectedFacilityId,
      supportsChildren,
    ]
  );
  const navGroups = React.useMemo(
    () => resolveAdminNavigation(navigationContext),
    [navigationContext]
  );
  const quickActions = React.useMemo(
    () => resolveAdminQuickActions(navigationContext),
    [navigationContext]
  );
  const brandHomeUrl = appendFacilityScopeToUrl(
    isPlatformUser ? "/operator/tenant-payments" : "/dashboard",
    selectedFacilityId || "all"
  );
  const settingsGroup = navGroups.find(
    (group) => group.id === "settings-and-access"
  );
  const primaryGroups = navGroups.filter(
    (group) => group.id !== "settings-and-access"
  );
  const destinations = React.useMemo(
    () => flattenAdminNavigation(navGroups),
    [navGroups]
  );
  const settingsDestinations = React.useMemo(
    () => (settingsGroup ? flattenAdminNavigation([settingsGroup]) : []),
    [settingsGroup]
  );
  const activeDestination = React.useMemo(
    () =>
      destinations
        .filter((destination) =>
          isAdminNavigationPathActive(location.pathname, destination.url)
        )
        .sort((a, b) => b.url.length - a.url.length)[0],
    [destinations, location.pathname]
  );
  const settingsDestinationIds = React.useMemo(
    () => new Set(settingsDestinations.map((destination) => destination.id)),
    [settingsDestinations]
  );
  const isSettingsMode = Boolean(
    !isPlatformUser &&
      activeDestination &&
      settingsDestinationIds.has(activeDestination.id)
  );
  const navigationPreferences = useNavigationPreferences({
    userKey: userInfo?.email ?? "",
    destinations,
  });

  React.useEffect(() => {
    if (isSettingsMode && !wasSettingsMode.current) {
      setOpen(true);
    }

    if (!isSettingsMode) {
      lastOperationalLocation.current = `${location.pathname}${location.search}${location.hash}`;
    }

    wasSettingsMode.current = isSettingsMode;
  }, [
    isSettingsMode,
    location.hash,
    location.pathname,
    location.search,
    setOpen,
  ]);

  const handleOpenSettings = () => {
    const destination = settingsDestinations[0];
    if (!destination) return;

    lastOperationalLocation.current = `${location.pathname}${location.search}${location.hash}`;
    setOpen(true);
    navigate(destination.url);
  };

  const handleCloseSettings = () => {
    setOpen(true);
    navigate(lastOperationalLocation.current);
  };

  return (
    <Sidebar
      collapsible="icon"
      data-staff-mode={mode}
      className={cn(isPlatformUser && "border-teal-950")}
    >
      <SidebarHeader className="border-sidebar-border gap-0 border-b p-0">
        <div className="border-sidebar-border bg-sidebar flex h-16 min-w-0 items-center gap-1 border-b px-2 group-data-[collapsible=icon]:h-auto group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-1.5">
          <Link
            to={brandHomeUrl}
            aria-label="スマコミ ホーム"
            className="focus-visible:ring-sidebar-ring group-data-[collapsible=icon]:min-h-7! flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 focus-visible:outline-none focus-visible:ring-2 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:px-0.5 group-data-[collapsible=icon]:py-0"
          >
            <img
              src="/logo.png"
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
              className="h-7 w-7 shrink-0 object-contain"
            />
            <span
              className="font-brand truncate text-[14px] font-extrabold tracking-[-0.03em] text-[#2ABAC9] group-data-[collapsible=icon]:hidden"
              translate="no"
            >
              スマコミ
            </span>
          </Link>
          <SidebarTrigger
            aria-label="サイドメニューを開閉"
            className="ml-auto size-11 shrink-0 group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:size-7 md:size-7"
          />
        </div>
        {!isPlatformUser ? (
          <div className="bg-sidebar px-2 pb-2 pt-1.5">
            <p className="text-sidebar-foreground/75 px-2 pb-1 text-[10px] font-semibold tracking-wide group-data-[collapsible=icon]:hidden">
              表示施設
            </p>
            <FacilitySwitcher />
          </div>
        ) : null}
        {isSettingsMode ? (
          <div className="bg-sidebar px-2 pb-2 pt-1 group-data-[collapsible=icon]:px-1">
            <SidebarMenu initial={false}>
              <SidebarMenuItem>
                <SidebarMenuButton
                  type="button"
                  tooltip="業務メニューへ戻る"
                  aria-label="業務メニューへ戻る"
                  onClick={handleCloseSettings}
                  className="min-h-10 cursor-pointer font-normal md:min-h-9"
                >
                  <ArrowLeft aria-hidden="true" />
                  <span>業務メニューへ戻る</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
        ) : (
          <div className="bg-sidebar flex gap-2 px-2 pb-2 pt-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:px-1">
            <NavigationSearch
              groups={navGroups}
              appearance="field"
              recentDestinations={navigationPreferences.recentDestinations}
              showShortcutHint={false}
            />
            <NavigationQuickCreate actions={quickActions} />
          </div>
        )}
      </SidebarHeader>
      <SidebarContent className="pt-1">
        {isSettingsMode ? (
          <NavigationSettings group={settingsGroup} />
        ) : (
          <>
            <NavigationFavorites
              destinations={destinations}
              favoriteIds={navigationPreferences.favoriteIds}
              favorites={navigationPreferences.favoriteDestinations}
              favoriteLimit={navigationPreferences.favoriteLimit}
              onToggleFavorite={navigationPreferences.toggleFavorite}
            />
            <NavMain groups={primaryGroups} />
          </>
        )}
      </SidebarContent>
      <SidebarFooter className="border-sidebar-border bg-sidebar gap-1 border-t">
        {!isSettingsMode ? (
          <NavigationSettingsEntry
            group={settingsGroup}
            onOpen={handleOpenSettings}
          />
        ) : null}
        <NavUser
          user={userData}
          isPlatformMode={isPlatformUser}
          canSwitchMode={canUseTenantMode && canUsePlatformMode}
          canManagePlan={!isPlatformUser && canManageBilling}
          isSwitchingMode={isSwitching}
          onModeSwitch={handleModeSwitch}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
